/**
 * MT Swiper Component
 * Responsive card swiper with configurable peek amounts
 */

class MTSwiper {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.track = wrapper.querySelector('.mt-image-swiper-track');
    this.originalSlides = Array.from(wrapper.querySelectorAll('.mt-image-swiper-slide'));
    this.prevBtn = wrapper.querySelector('.mt-image-swiper-prev');
    this.nextBtn = wrapper.querySelector('.mt-image-swiper-next');
    this.pagination = wrapper.closest('.mt-image-swiper-section').querySelector('.mt-image-swiper-pagination');
    
    // 生成唯一ID防止多实例冲突
    this.instanceId = 'mt-swiper-' + Math.random().toString(36).substr(2, 9);
    this.wrapper.setAttribute('data-swiper-id', this.instanceId);
    
    // Configuration from data attributes
    this.config = {
      desktopPeek: parseInt(wrapper.dataset.desktopPeek) || 80,
      mobilePeek: parseInt(wrapper.dataset.mobilePeek) || 60,
      cardGap: parseInt(wrapper.dataset.cardGap) || 20,
      autoplay: wrapper.dataset.autoplay === 'true',
      autoplaySpeed: parseInt(wrapper.dataset.autoplaySpeed) || 5000
    };
    
    this.currentIndex = 0;
    this.isTransitioning = false;
    this.autoplayTimer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.slides = [];
    
    this.init();
  }
  
  init() {
    if (this.originalSlides.length === 0) return;
    
    this.cloneSlides();
    this.setupCSS();
    this.createPagination();
    this.attachEvents();
    this.setupVideoPlayers();
    this.handleVideoAutoplay();
    
    // Start at the first real slide (after cloned last slide)
    this.currentIndex = 1;
    this.updateSlider(false);
    
    if (this.config.autoplay) {
      this.startAutoplay();
    }
    
    // Update on window resize with debounce - 绑定到实例
    this.resizeHandler = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.setupCSS();
        this.updateSlider(false);
      }, 250);
    };
    window.addEventListener('resize', this.resizeHandler);
  }
  
  handleVideoAutoplay() {
    // Try to autoplay videos in the active slide
    const tryAutoplay = () => {
      const activeSlide = this.slides[this.currentIndex];
      if (!activeSlide) return;
      
      const video = activeSlide.querySelector('.mt-image-card-video-player[data-autoplay="true"]');
      if (video) {
        // Ensure video is muted for autoplay to work
        video.muted = true;
        
        // Try to play
        const playPromise = video.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('Video autoplay started successfully');
            })
            .catch(error => {
              console.log('Video autoplay prevented by browser:', error);
              // If autoplay fails, show play button or controls
              video.controls = true;
            });
        }
      }
    };
    
    // Try autoplay after a short delay to ensure DOM is ready
    setTimeout(tryAutoplay, 500);
    
    // Also try on user interaction (click anywhere)
    const enableAutoplayOnInteraction = () => {
      tryAutoplay();
      document.removeEventListener('click', enableAutoplayOnInteraction);
      document.removeEventListener('touchstart', enableAutoplayOnInteraction);
    };
    
    document.addEventListener('click', enableAutoplayOnInteraction, { once: true });
    document.addEventListener('touchstart', enableAutoplayOnInteraction, { once: true });
  }
  
  setupVideoPlayers() {
    // Setup YouTube video players with poster
    const youtubeWrappers = this.wrapper.querySelectorAll('.youtube-video');
    youtubeWrappers.forEach(wrapper => {
      const playBtn = wrapper.querySelector('.youtube-play-btn');
      const autoplay = wrapper.dataset.autoplay === 'true';
      
      if (playBtn) {
        playBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const videoId = wrapper.dataset.videoId;
          
          if (!videoId) {
            console.error('YouTube video ID not found');
            return;
          }
          
          const iframe = document.createElement('iframe');
          iframe.className = 'mt-image-card-video-iframe';
          iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0&modestbranding=1`;
          iframe.frameBorder = '0';
          iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
          iframe.allowFullscreen = true;
          
          // Clear wrapper and add iframe
          wrapper.innerHTML = '';
          wrapper.appendChild(iframe);
        });
      }
      
      // Auto-load YouTube iframe if no poster and autoplay enabled
      if (!playBtn && autoplay) {
        const existingIframe = wrapper.querySelector('iframe');
        if (existingIframe) {
          // Iframe already exists, just ensure it has autoplay params
          console.log('YouTube iframe with autoplay loaded');
        }
      }
    });
    
    // Setup Instagram video players - Load Instagram embed script
    const instagramWrappers = this.wrapper.querySelectorAll('.instagram-video');
    if (instagramWrappers.length > 0) {
      // Check if we have any embeds without posters
      const needsEmbed = Array.from(instagramWrappers).some(wrapper => {
        return wrapper.querySelector('.instagram-media');
      });
      
      if (needsEmbed) {
        // Load Instagram embed script if not already loaded
        if (!window.instgrm && !document.querySelector('script[src*="instagram.com/embed.js"]')) {
          const script = document.createElement('script');
          script.async = true;
          script.src = '//www.instagram.com/embed.js';
          document.body.appendChild(script);
          
          script.onload = () => {
            if (window.instgrm) {
              window.instgrm.Embeds.process();
            }
          };
        } else if (window.instgrm) {
          // Process embeds if script already loaded
          setTimeout(() => {
            window.instgrm.Embeds.process();
          }, 100);
        }
      }
    }
    
    // Pause videos when sliding away
    this.wrapper.addEventListener('slideChange', () => {
      this.pauseAllVideos();
    });
  }
  
  pauseAllVideos() {
    // Pause local videos
    const videos = this.wrapper.querySelectorAll('.mt-image-card-video-player');
    videos.forEach(video => {
      if (!video.paused) {
        video.pause();
      }
    });
    
    // For YouTube/Instagram iframes, we can't directly control them,
    // but we can reload the wrapper to stop playback if needed
  }
  
  cloneSlides() {
    // Clone first and last slides for infinite loop effect
    const firstClone = this.originalSlides[0].cloneNode(true);
    const lastClone = this.originalSlides[this.originalSlides.length - 1].cloneNode(true);
    
    firstClone.classList.add('clone');
    lastClone.classList.add('clone');
    
    // Add clones to DOM
    this.track.appendChild(firstClone);
    this.track.insertBefore(lastClone, this.track.firstChild);
    
    // Update slides array to include clones
    this.slides = Array.from(this.track.querySelectorAll('.mt-image-swiper-slide'));
  }
  
  setupCSS() {
    const isMobile = window.innerWidth < 768;
    const peek = isMobile ? this.config.mobilePeek : this.config.desktopPeek;
    
    // 在当前section作用域内设置CSS变量值，避免影响其他实例
    const section = this.wrapper.closest('.mt-image-swiper-section');
    
    // 更新当前section的CSS变量值
    if (isMobile) {
      section.style.setProperty('--mobile-peek', `${this.config.mobilePeek}px`);
      section.style.setProperty('--card-gap', `${this.config.cardGap}px`);
      
      const slideWidth = window.innerWidth - this.config.mobilePeek - 20 - this.config.cardGap;
      this.slides.forEach(slide => {
        slide.style.width = `${slideWidth}px`;
      });
    } else {
      section.style.setProperty('--desktop-peek', `${this.config.desktopPeek}px`);
      section.style.setProperty('--card-gap', `${this.config.cardGap}px`);
      
      const containerWidth = this.wrapper.offsetWidth - 40; // minus padding
      const slideWidth = containerWidth - (this.config.desktopPeek * 2) - this.config.cardGap;
      this.slides.forEach(slide => {
        slide.style.width = `${slideWidth}px`;
      });
    }
  }
  
  createPagination() {
    if (!this.pagination) return;
    
    this.pagination.innerHTML = '';
    // Only create dots for original slides (not clones)
    this.originalSlides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.classList.add('mt-image-swiper-dot');
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      if (index === 0) dot.classList.add('active');
      
      dot.addEventListener('click', () => this.goToSlide(index + 1)); // +1 because of cloned last slide at start
      this.pagination.appendChild(dot);
    });
    
    this.dots = Array.from(this.pagination.querySelectorAll('.mt-image-swiper-dot'));
  }
  
  attachEvents() {
    // Navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }
    
    // Touch events for mobile swipe
    this.track.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
      this.stopAutoplay();
    }, { passive: true });
    
    this.track.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
      if (this.config.autoplay) {
        this.startAutoplay();
      }
    }, { passive: true });
    
    // Mouse drag for desktop (optional)
    let isDragging = false;
    let startX = 0;
    let currentX = 0;
    
    this.track.addEventListener('mousedown', (e) => {
      isDragging = true;
      startX = e.pageX;
      this.track.style.cursor = 'grabbing';
      this.stopAutoplay();
    });
    
    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      currentX = e.pageX - startX;
    });
    
    document.addEventListener('mouseup', () => {
      if (!isDragging) return;
      isDragging = false;
      this.track.style.cursor = 'grab';
      
      if (Math.abs(currentX) > 50) {
        if (currentX > 0) {
          this.prev();
        } else {
          this.next();
        }
      }
      
      currentX = 0;
      if (this.config.autoplay) {
        this.startAutoplay();
      }
    });
    
    // Pause autoplay on hover
    if (this.config.autoplay) {
      this.wrapper.addEventListener('mouseenter', () => this.stopAutoplay());
      this.wrapper.addEventListener('mouseleave', () => this.startAutoplay());
    }
  }
  
  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }
  }
  
  prev() {
    if (this.isTransitioning) return;
    
    this.pauseAllVideos();
    this.currentIndex--;
    this.updateSlider();
    
    // If we're at the cloned last slide (index 0), jump to real last slide
    if (this.currentIndex === 0) {
      setTimeout(() => {
        this.currentIndex = this.slides.length - 2; // Real last slide
        this.updateSlider(false);
      }, 500);
    }
  }
  
  next() {
    if (this.isTransitioning) return;
    
    this.pauseAllVideos();
    this.currentIndex++;
    this.updateSlider();
    
    // If we're at the cloned first slide (last index), jump to real first slide
    if (this.currentIndex === this.slides.length - 1) {
      setTimeout(() => {
        this.currentIndex = 1; // Real first slide
        this.updateSlider(false);
      }, 500);
    }
  }
  
  goToSlide(index) {
    if (this.isTransitioning || index === this.currentIndex) return;
    
    this.currentIndex = index;
    this.updateSlider();
  }
  
  updateSlider(animate = true) {
    if (!animate) {
      this.track.style.transition = 'none';
    } else {
      this.track.style.transition = '';
    }
    
    const isMobile = window.innerWidth < 768;
    let offset = 0;
    
    // Calculate offset based on current slide
    for (let i = 0; i < this.currentIndex; i++) {
      offset += this.slides[i].offsetWidth;
    }
    
    // Apply transform
    if (isMobile) {
      // Mobile: slides are left-aligned
      this.track.style.transform = `translateX(-${offset}px)`;
    } else {
      // Desktop: center the current slide
      this.track.style.transform = `translateX(-${offset}px)`;
    }
    
    // Update active states
    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentIndex;
      slide.classList.toggle('active', isActive);
      
      // Handle video autoplay for active slide
      if (isActive) {
        const video = slide.querySelector('.mt-image-card-video-player[data-autoplay="true"]');
        if (video && video.paused) {
          video.muted = true; // Ensure muted for autoplay
          video.play().catch(e => {
            console.log('Autoplay prevented:', e);
            video.controls = true; // Show controls if autoplay fails
          });
        }
      }
    });
    
    // Update pagination dots (map current index to original slide index)
    if (this.dots) {
      let realIndex = this.currentIndex - 1; // Adjust for cloned last slide at start
      if (realIndex < 0) realIndex = this.originalSlides.length - 1;
      if (realIndex >= this.originalSlides.length) realIndex = 0;
      
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === realIndex);
      });
    }
    
    // Navigation buttons always enabled for infinite loop
    if (this.prevBtn) {
      this.prevBtn.disabled = false;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = false;
    }
    
    // Reset transition
    if (!animate) {
      setTimeout(() => {
        this.track.style.transition = '';
      }, 50);
    }
    
    // Transition lock
    this.isTransitioning = true;
    setTimeout(() => {
      this.isTransitioning = false;
    }, 500);
  }
  
  startAutoplay() {
    if (!this.config.autoplay) return;
    
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.next(); // Simply call next() - it handles infinite loop
    }, this.config.autoplaySpeed);
  }
  
  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
  
  destroy() {
    this.stopAutoplay();
    // 移除事件监听器避免内存泄漏
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    // 清理当前实例的CSS变量
    const section = this.wrapper.closest('.mt-image-swiper-section');
    if (section) {
      section.style.removeProperty('--desktop-peek');
      section.style.removeProperty('--mobile-peek');
      section.style.removeProperty('--card-gap');
    }
  }
}

// 全局实例管理，防止重复初始化
window.mtSwiperInstances = window.mtSwiperInstances || new Map();

// Initialize all swipers on the page
document.addEventListener('DOMContentLoaded', () => {
  const wrappers = document.querySelectorAll('.mt-image-swiper-wrapper:not([data-swiper-initialized])');
  wrappers.forEach(wrapper => {
    const instance = new MTSwiper(wrapper);
    wrapper.setAttribute('data-swiper-initialized', 'true');
    // 存储实例以便后续管理
    const sectionId = wrapper.closest('.mt-image-swiper-section')?.dataset?.sectionId;
    if (sectionId) {
      window.mtSwiperInstances.set(sectionId, instance);
    }
  });
});

// Re-initialize on Shopify section load (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const wrapper = event.target.querySelector('.mt-image-swiper-wrapper:not([data-swiper-initialized])');
    if (wrapper) {
      const instance = new MTSwiper(wrapper);
      wrapper.setAttribute('data-swiper-initialized', 'true');
      
      const sectionId = event.target.dataset?.sectionId;
      if (sectionId) {
        // 清理旧实例
        const oldInstance = window.mtSwiperInstances.get(sectionId);
        if (oldInstance) {
          oldInstance.destroy();
        }
        // 存储新实例
        window.mtSwiperInstances.set(sectionId, instance);
      }
    }
  });
  
  // 清理卸载的section
  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.target.dataset?.sectionId;
    if (sectionId) {
      const instance = window.mtSwiperInstances.get(sectionId);
      if (instance) {
        instance.destroy();
        window.mtSwiperInstances.delete(sectionId);
      }
    }
  });
}
